import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MultiDatePicker } from "@/components/MultiDatePicker";

export const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const fasce = (l) => l.map((s) => `${s.start}-${s.end}`).join(" e ");

export const recurLabel = (r) => {
  const ds = r.day_shifts || {};
  const std = r.weekdays.filter((d) => !ds[d]);
  const parts = std.length ? [`${std.map((d) => WEEKDAYS[d]).join(", ")} · ${fasce(r.shifts)}`] : [];
  return [...parts, ...r.weekdays.filter((d) => ds[d]).map((d) => `${WEEKDAYS[d]} · ${fasce(ds[d])}`)].join(" | ") + (r.closed_dates?.length ? ` · ${r.closed_dates.length} chiusure` : "");
};

function ShiftList({ list, onChange, testid }) {
  const setShift = (i, k, v) => onChange(list.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2">
      {list.map((s, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${testid}-${i}`}>
          <Input type="time" value={s.start} onChange={(e) => setShift(i, "start", e.target.value)} className="w-28" data-testid={`${testid}-start-${i}`} />
          <span className="text-slate-400">–</span>
          <Input type="time" value={s.end} onChange={(e) => setShift(i, "end", e.target.value)} className="w-28" data-testid={`${testid}-end-${i}`} />
          {list.length > 1 && <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}>Rimuovi</Button>}
        </div>
      ))}
      {list.length < 4 && <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => onChange([...list, { start: "16:00", end: "20:00" }])} data-testid={`${testid}-add`}>+ fascia</Button>}
    </div>
  );
}

export function RecurringFields({ value, onChange, hideStart = false }) {
  const set = (p) => onChange({ ...value, ...p });
  const ds = value.day_shifts || {};
  const toggle = (d) => set({ weekdays: value.weekdays.includes(d) ? value.weekdays.filter((x) => x !== d) : [...value.weekdays, d].sort() });
  const setDay = (d, list) => {
    const next = { ...ds };
    if (list) next[d] = list; else delete next[d];
    set({ day_shifts: next });
  };
  return (
    <div className="space-y-4" data-testid="recurring-fields">
      <div className="space-y-2"><Label>Giorni della settimana</Label>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((l, d) => (
            <button key={l} type="button" onClick={() => toggle(d)} data-testid={`recur-day-${d}`}
              className={`h-10 w-14 rounded-full border text-sm font-semibold transition-colors ${value.weekdays.includes(d) ? "border-[#61a229] bg-[#61a229] text-white" : "bg-white text-slate-600 hover:border-[#61a229]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2"><Label>Fasce orarie standard</Label>
        <ShiftList list={value.shifts} onChange={(shifts) => set({ shifts })} testid="recur-shift" />
      </div>
      {value.weekdays.length > 0 && (
        <div className="space-y-2" data-testid="recur-day-overrides"><Label>Orario diverso per alcuni giorni (facoltativo)</Label>
          {value.weekdays.map((d) => (
            <div key={d} className="rounded-xl border px-3 py-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!ds[d]} onChange={(e) => setDay(d, e.target.checked ? [{ start: "09:00", end: "13:00" }] : null)} data-testid={`recur-override-${d}`} />
                <b className="w-10">{WEEKDAYS[d]}</b>
                <span className="text-slate-500">{ds[d] ? "orario dedicato" : `standard · ${fasce(value.shifts)}`}</span>
              </label>
              {ds[d] && <div className="mt-2"><ShiftList list={ds[d]} onChange={(l) => setDay(d, l)} testid={`recur-day${d}-shift`} /></div>}
            </div>
          ))}
        </div>
      )}
      <div className="space-y-3 rounded-xl border bg-slate-50 p-3" data-testid="recur-closures">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><b>Salta le festività nazionali</b><span className="block text-xs text-slate-500">Capodanno, Epifania, Pasqua e Pasquetta, 25 aprile, 1 maggio, 2 giugno, Ferragosto, 1 novembre, 8, 25 e 26 dicembre</span></span>
          <Switch checked={value.skip_holidays !== false} onCheckedChange={(v) => set({ skip_holidays: v })} data-testid="recur-skip-holidays" />
        </label>
        <div className="space-y-2"><Label>Altre chiusure (es. patrono, ferie del negozio)</Label>
          <MultiDatePicker value={value.closed_dates || []} onChange={(closed_dates) => set({ closed_dates })} hint="Tocca i giorni in cui il negozio resta chiuso: in quei giorni non vengono creati turni." />
          <p className="text-xs text-slate-500">Nei giorni di chiusura non vengono creati turni. Se aggiungi una chiusura dopo, i turni di quel giorno vengono tolti.</p>
        </div>
      </div>
      {!hideStart && <div className="max-w-xs space-y-2"><Label>Dal giorno</Label><Input type="date" value={value.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="recur-start-date" /></div>}
      <p className="text-xs text-slate-500">Il calendario si rinnova da solo ogni settimana finché non termini l'orario ripetitivo. Se assegni a un dipendente un lavoro esterno in questi orari, il lavoro esterno ha la precedenza e qui risulta "impegnato fuori".</p>
    </div>
  );
}
